import React from 'react';
import { clientDashboardExtensionSlots } from '@sharefiledev/client-dashboard';
import { CodeBlock } from '@sharefiledev/icons';
import {
	type DocumentRequestMenuExtensionParams,
	piletOverlays,
	type RequestListAttachmentCountParams,
	requestListExtensionSlots,
	requestListPiletCapabilities,
} from '@sharefiledev/request-list-pilet-sdk';
import { RID } from '@sharefiledev/sfplugin-resource-id';
import type { PiletApi } from '@sharefiledev/sharefile-appshell';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';
import { addBlocks, AsyncRequestListsBlock } from './Blocks';
import {
	attachmentCapabilities,
	itemCapabilities,
	requestListCapabilities,
} from './capabilities';
import {
	AsyncAttachmentValidationLabel,
	AsyncClientDashboardAttachmentsDrawerExtensionSlot,
	AsyncClientDashboardAttachmentsTabLabelExtensionSlot,
	AsyncDocumentRequestActionsMenu,
	AsyncProjectDeletedResourceTabExtensionSlot,
	AsyncProjectRequestListTileExtensionSlot,
	AsyncRequestListTabExtensionSlot,
	AsyncShareButtonExtensionSlot,
	AsyncSpecEditorExtensionSlot,
	AsyncTaskBadgeExtensionSlot,
	AsyncTaskProgressBarExtension,
} from './Extensions';
import { setLogger } from './logger';
import {
	AsyncClientRequestListDrawerViewLoaderBlock,
	AsyncCreateRequestListDrawerBlock,
	AsyncRequestListDrawerViewLoaderBlock,
} from './Overlays';
import { AsyncDemoPage, AsyncRequestListPage, AsyncRequestListsPage } from './Pages';
import {
	configureHttp,
	DefaultRequestListDataProvider,
} from './PluginDataProvider/DefaultRequestListDataProvider';
import { RequestListEntityType } from './types';

// Necessary to support various date functions in request list pilet with AntD
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);

const queryClient = new QueryClient();

export function setup(app: PiletApi) {
	setLogger(app.sf.getLogger());
	// register all blocks
	addBlocks(app);

	app.registerPage('/requestlists/:requestListId', () => (
		<QueryClientProvider client={queryClient}>
			<AsyncRequestListPage piletApi={app} />
		</QueryClientProvider>
	));
	app.registerPage('/requestlists', () => (
		<QueryClientProvider client={queryClient}>
			<AsyncRequestListsPage piletApi={app} />
		</QueryClientProvider>
	));

	// document requests tab
	app.registerExtension(requestListExtensionSlots.projectTab, ({ params }) => (
		<AsyncRequestListTabExtensionSlot {...params} piletApi={app} />
	));

	// document requests tile
	app.registerExtension(requestListExtensionSlots.projectTile, ({ params }) => (
		<AsyncProjectRequestListTileExtensionSlot piletApi={app} {...params} />
	));

	app.registerExtension(
		requestListExtensionSlots.recentlyDeletedTab,
		({ params }) => (
			<AsyncProjectDeletedResourceTabExtensionSlot piletApi={app} {...params} />
		),
		{ piletSortWeight: 100 }
	);

	app.registerExtension<RequestListAttachmentCountParams>(
		requestListExtensionSlots.attachmentsTabLabel,
		({ params }) => (
			<AsyncClientDashboardAttachmentsTabLabelExtensionSlot piletApi={app} {...params} />
		)
	);

	app.registerExtension(
		clientDashboardExtensionSlots.drawerAttachmentsContent,
		({ params }) => (
			<AsyncClientDashboardAttachmentsDrawerExtensionSlot piletApi={app} {...params} />
		)
	);

	app.registerExtension(requestListExtensionSlots.taskBadge, ({ params }) => {
		return <AsyncTaskBadgeExtensionSlot piletApi={app} {...params} />;
	});

	app.registerExtension(requestListExtensionSlots.viewListBlock, ({ params }) => (
		<QueryClientProvider client={queryClient}>
			<AsyncRequestListsBlock {...params} piletApi={app} />
		</QueryClientProvider>
	));
	app.registerExtension(
		requestListExtensionSlots.deprecated_viewListBlock,
		({ params }) => <AsyncRequestListsBlock {...params} piletApi={app} />
	);

	app.registerExtension(requestListExtensionSlots.shareButton, ({ params }) => {
		return <AsyncShareButtonExtensionSlot piletApi={app} {...params} />;
	});

	app.registerExtension(requestListExtensionSlots.specEditor, ({ params }) => {
		return (
			<QueryClientProvider client={queryClient}>
				<AsyncSpecEditorExtensionSlot piletApi={app} {...params} />
			</QueryClientProvider>
		);
	});

	app.registerExtension(requestListExtensionSlots.taskProgressBar, ({ params }) => {
		return <AsyncTaskProgressBarExtension piletApi={app} {...params} />;
	});

	// SFAI-703
	app.registerExtension(
		requestListExtensionSlots.attachmentValidationLabel,
		({ params }) => {
			return <AsyncAttachmentValidationLabel piletApi={app} {...params} />;
		}
	);

	app.registerExtension<DocumentRequestMenuExtensionParams>(
		requestListExtensionSlots.contextActionsMenu,
		({ params }) => {
			return (
				<AsyncDocumentRequestActionsMenu piletApi={app} resourceId={params.resourceId} />
			);
		}
	);

	app.registerDrawer(
		piletOverlays.createRequestListDrawer,
		({ options, setWidth, completeInteraction, cancelInteraction }) => {
			return (
				<AsyncCreateRequestListDrawerBlock
					piletApi={app}
					setWidth={setWidth}
					cancelInteraction={cancelInteraction}
					completeInteraction={completeInteraction}
					containerRID={options?.params.containerRID!}
					originAnalyticsTrackingId={options?.params.originAnalyticsTrackingId || ''}
				/>
			);
		},
		/**
		 * reusing existing components for adding and modifying a request list
		 * existing components still use flow-web which use modals with z-index of 10
		 * we need to put this drawer behind those modals for them to show
		 */
		{ drawerProps: { zIndex: 9, width: '500px' } }
	);

	app.registerDrawer(
		piletOverlays.viewRequestListDrawer,
		({ options, completeInteraction }) => {
			return (
				<AsyncRequestListDrawerViewLoaderBlock
					piletApi={app}
					containerRID={options?.containerRID!}
					completeInteraction={completeInteraction}
					verticalContextKey={options?.verticalContextKey}
					originAnalyticsTrackingId={options?.originAnalyticsTrackingId || ''}
				/>
			);
		},
		{
			/**
			 * reusing existing components for adding and modifying a request list
			 * existing components still use flow-web which use modals with z-index of 10
			 * we need to put this drawer behind those modals for them to show
			 */
			drawerProps: {
				width: '100%',
				title: 'Document request',
				zIndex: 9,
				push: false,
			},
		}
	);

	app.registerDrawer(
		piletOverlays.clientViewRequestListDrawer,
		({ options, completeInteraction, updateDrawerProps }) => {
			return (
				<AsyncClientRequestListDrawerViewLoaderBlock
					containerRID={options?.params.containerRID!}
					piletApi={app}
					onCompletion={completeInteraction}
					updateDrawerProps={updateDrawerProps}
					navigationContext={options?.params.navigationContext}
				/>
			);
		},
		{ drawerProps: { width: '640px', zIndex: 9 } }
	);

	app.sf.registerViewEngineResourceURLRewrite({
		resourceType: 'requestlist',
		defaultPath: '/requestlists/{requestListId}',
		callback: async (rid: string) => {
			const parsed = RID.parse(rid);
			const requestListRID = parsed.findEntityType(RequestListEntityType.requestlist);
			return await DefaultRequestListDataProvider.getRequestList(
				requestListRID!.entityId
			);
		},
	});

	app.sf.registerViewEngineResourceURLRewrite({
		resourceType: 'requestlist_item',
		defaultPath: '/requestlists/{requestListId}?task={requestListTaskId}',
		clientPath: '?taskRID={resourceId}&taskName={title}&taskType=requestlist_item',
		callback: async (rid: string) => {
			const parsed = RID.parse(rid);
			const requestListRID = parsed.findEntityType(RequestListEntityType.requestlist);
			const requestListItemRID = parsed.findEntityType(RequestListEntityType.item);
			const requestList = await DefaultRequestListDataProvider.getRequestList(
				requestListRID!.entityId
			);
			return await DefaultRequestListDataProvider.getTask(
				requestList.requestListId,
				requestList.resourceId,
				requestListItemRID!.entityId
			);
		},
	});

	app.sf.registerCapability(
		requestListPiletCapabilities.requestListCapabilities,
		requestListCapabilities
	);
	app.sf.registerCapability(
		requestListPiletCapabilities.itemCapabilities,
		itemCapabilities
	);
	app.sf.registerCapability(
		requestListPiletCapabilities.attachmentCapabilities,
		attachmentCapabilities
	);

	if (process.env.NODE_ENV === 'development') {
		app.sf.registerLeftNavComponent({
			href: '/requestlist-dev',
			title: () => 'Request List Dev',
			icon: CodeBlock,
			weight: 50,
		});

		app.registerPage('/requestlist-dev', () => <AsyncDemoPage piletApi={app} />);

		if (typeof app.meta.config.apiOrigin === 'string') {
			configureHttp(config => {
				config.origin = app.meta.config.apiOrigin;
			});
		}
	}
}
